import {PortableText} from '@portabletext/react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {useSubdomainBrand} from '~/hooks/useSubdomainBrand';

import {OptimizedImageWrapper} from '~/components/global/OptimizedImageWrapper.client';

import {sanityLinkToAnchor} from '~/utils/link';

/**
 * BodyText component
 * Renders a relatively simple subset of `<PortableText />` options presented to a
 * Sanity content editor, respecting bold, italics, lists, and links.
 */

export function BodyText({sanityModule}) {
  const {body, isCentered} = sanityModule ?? {};

  // If the block is essentially empty markup, we can avoid rendering anything.
  // NOTE: if more annotation marks or child types are added, this code needs a critical eye for updates
  if (
    body.every((b) => {
      return (
        b.children?.every((c) => {
          if (c._type === 'image') return c.asset?._ref?.length === 0 ?? true;
          else return c.text?.length === 0 ?? true;
        }) ?? true
      );
    })
  ) {
    return null;
  }

  return (
    <div
      className={clsx(
        'portable-body-text',
        isCentered && 'portable-body-text--centered',
      )}
    >
      <PortableText
        value={body}
        components={{
          types: {
            image: Image,
          },
          marks: {
            annotationLinkBlog: AnnotationLink,
            annotationLinkInternal: AnnotationLink,
            annotationLinkExternal: AnnotationLink,
          },
        }}
      />
    </div>
  );
}

const Image = ({value}) => {
  return (
    <OptimizedImageWrapper
      classNames="portable-body-text__image"
      imageUrl={value.asset?.url}
    />
  );
};

const AnnotationLink = ({value, children}) => {
  const brand = useSubdomainBrand();
  return (
    <a
      {...sanityLinkToAnchor(value, brand)}
      className="portable-body-text__link"
    >
      {children}
    </a>
  );
};

BodyText.propTypes = {
  sanityModule: PropTypes.object.isRequired,
};
